export interface GoogleAnalyticsEvent {
  event: GAEventKeys | string;
  timestamp?: number;
  tournament_id?: string;
  type_of_tournament?: string;
  total_prize?: number;
  game?: string;
  element?: string;
  set_up_wallet?: string;
  user_name?: string;
  user_type?: string;
  sponsor?: string;
  tournament_name?: string;
  tournament_type?: string;
  instance_id?: string;
  user_id?: string;
  number_of_player?: number;
  founded?: number;
  entry_amount?: number;
  transaction_fee_or_taxes?: number;
  pass_entries?: number;
  pass_id?: string;
  pass_name?: string;
  pass_price?: string;
  promo_code?: string;
  promo_code_percent?: number;
  fantaz_pass_id?: string;
  ga_value?: string;
  page_title?: string;
  hostname?: string;
  path?: string;
  url?: string;
  onboarding?: boolean;
  terms_and_conditions_version?: string;
  verification_attempts_failed?: number;
  ecommerce?: {
    transaction_id?: string;
    value?: number;
    tax?: number;
    affiliation?: string;
    currency?: string;
    items: Ecommerce[];
  };
  [key: string]: any;
}

export interface Ecommerce {
  item_name: string;
  item_id: number;
  price: number;
  item_category: string;
}

// TODO: Move to shared static dependency
export enum GAEventKeys {
  USER_BUYS_PASS = 'user_buys_tournament_pass',
  USER_BUYS_PASS_USING_CODE = 'user_buys_tournament_pass_using_code',
  USER_SIGN_IN = 'user_sign_in',
  USER_NEW_SIGNUP = 'user_new_signup',
  USER_FILED_SIGNUP = 'user_failed_signup',
  GAME_EVENT = 'game_event',
  HOME_SLIDER = 'home_slider',
  CUSTOM_CLICK = 'custom-click',
  USER_ONBOARDING = 'user_onboarding',
  USER_VERIFICATION_APPROVED = 'user_verification_approved',
  USER_CONVERT_ZBO = 'user_convert_zbo',
  USER_CANCEL_ZBO_SUBSCRIPTION = 'user_cancel_zbo_subscription',
  PROMO_CODE = 'promo_code',
  PAGE_VIEW = 'pageview',
  USER_LOCATION_SHARED = 'user_location_shared',
  USER_LOCATION_SHARED_FAIL = 'user_location_shared_fail',
  USER_LOCATION_INACCESSIBLE = 'user_location_inaccessible',
  USER_LOCATION_NOT_SHARING = 'user_location_initially_not_sharing_popup_help',
  USER_LOCATION_BLOCKED = 'user_location_not_allowed_to_see_cash_tournament_off_limits',
  USER_LOCATION_FALLBACK_BY_IP = 'user_location_fallback_shared_by_ip',
  USER_LOAD_APP = 'user_load_app',
  USER_LOGOUT = 'user_logout',
  USER_JOINS_TOURNAMENT = 'user_joins_tournament',
  USER_JOINS_TOURNAMENT_BY_PASS = 'user_joins_tournament_by_pass',
  PURCHASE = 'purchase',
  USER_COMPLETED_FUNDS_Z_WALLET = 'user_completed_funds_zwallet',
  USER_INIT_FUNDS_Z_WALLET = 'user_init_funds_zwallet',
  USER_REQUEST_ZENDESK = 'user_request_zendesk',
  ENDED_PROMOTE_VIDEO = 'ended_promote_video',
  PLAY_PROMOTE_VIDEO = 'play_promote_video',
  PAUSED_PROMOTE_VIDEO = 'paused_promote_video',
  USER_SHARE_PROFILE_LINK = 'user_share_profile_link',
  USER_SUBSCRIBES_TOURNAMENT = 'user_subscribes_tournament',
  USER_UNSUBSCRIBES_TOURNAMENT = 'user_unsubscribes_tournament',
  USER_NEEDS_NEW_TERMS = 'user_needs_new_terms',
  USER_ACCEPTS_NEW_TERMS = 'user_accepts_new_terms',
  USER_TRY_FOR_FREE = 'user_try_for_free',
  ADD_TO_CARD = 'add_to_cart',
  USER_CONVERT_Z_PLAYER = 'user_convert_z_player',
  USER_WITHDRAWLS_MONEY = 'user_withdrawls_money',
  USER_FAILED_WITHDRAWL = 'user_failed_withdrawl',
  GAME_WRAPPER_IDENTITY = 'game_wrapper_identify',
  TRANSACTION = 'transaction',
  USER_VERIFICATION_STATUS = 'user_verification_status',
  USER_PROFILE = 'user_profile',
  PAYMENT_REGISTRATION = 'payment_registration',
  DEPOSIT_FAIL = 'deposit_fail',
  COMPLETE_WITHDRAW_FAIL = 'complete_withdraw_fail',
  SMARTLOOK_REQUIRED = 'smartlook_required',
  DEPOSIT_CARD_TYPE_REJECTED = 'deposit_card_type_rejected',
  DEPOSIT_CARD_TYPE_RECOVERED = 'deposit_card_type_recovered',
  DEPOSIT_CARD_TYPE_ABANDONED = 'deposit_card_type_abandoned',
}

export interface SmartlookInfo {
  advancedNetwork: boolean;
  dashboardHost: string;
  key: string;
  ok: boolean;
  playUrl: string;
  recordId: string;
  recording: {
    analytics: boolean;
    assetsHost: string;
    identifyApi: boolean;
    maxRecordDuration: number;
    maxSessionDuration: number;
    recordConsole: boolean;
    recordNetwork: boolean;
    recordTimeout: number;
    sessionTimeout: number;
    storeGroup: string;
    writerHost: string;
  };
  recordingAllowed: boolean;
  recordingRules: [];
  serverTime: number;
  sessionId: string;
  version: string;
  visitorId: string;
}

export class GoogleAnalyticEvent {
  event: GAEventKeys | string;
  timestamp?: number;

  constructor(
    event: GAEventKeys,
    metadata: Partial<GoogleAnalyticsEvent> & { [key: string]: any } = {},
  ) {
    this.event = event;
    this.timestamp = Date.now();
    Object.assign(this, metadata);
  }
}

/**
 * Google Analytics event for a user sign up.
 *
 * @param {object} data - The data for the event.
 * @property {string} data.user_id - The user ID.
 * @property {string} data.idp - The IDP.
 * @property {string} data.promo_code - The promo code.
 * @property {string} data.sponsor - The sponsor.
 * @property {string} data.registration_type - The registration type.
 */
export class SignUpGoogleAnalyticEvent extends GoogleAnalyticEvent {
  constructor(metadata: {
    user_id: string;
    idp: string;
    promo_code?: string;
    sponsor?: string;
    registration_type: string;
    registration_medium: string;
  }) {
    super(GAEventKeys.USER_NEW_SIGNUP, {
      ...metadata,
    });
  }
}

export class CompleteSignUpGoogleAnalyticEvent extends GoogleAnalyticEvent {
  constructor(metadata: Partial<GoogleAnalyticsEvent> = {}) {
    super(GAEventKeys.USER_NEW_SIGNUP, {
      ...metadata,
      eventCategory: 'Sign-up',
      eventAction: 'Completed',
    });
  }
}

export class LoadUserGoogleAnalyticEvent extends GoogleAnalyticEvent {
  constructor(metadata: Partial<GoogleAnalyticsEvent> = {}) {
    super(GAEventKeys.USER_LOAD_APP, {
      ...metadata,
      eventCategory: 'Sign-up',
      eventAction: 'Completed',
    });
  }
}

export class LogoutUserGoogleAnalyticEvent extends GoogleAnalyticEvent {
  constructor(metadata: Partial<GoogleAnalyticsEvent> = {}) {
    super(GAEventKeys.USER_LOGOUT, {
      ...metadata,
    });
  }
}
